/**
 * SEO Utilities
 * Handles SEO metadata management, structured data, and performance optimization
 */

import { SEOMetaTags, OpenGraphTags, TwitterCardTags, StructuredData } from '../../../shared/interfaces/SEOManagement';

export interface SEOPageData {
  meta: SEOMetaTags;
  openGraph: OpenGraphTags;
  twitter: TwitterCardTags;
  structuredData: StructuredData[];
}

export class SEOUtils {
  private static instance: SEOUtils;
  private cache: Map<string, SEOPageData> = new Map();
  private cacheExpiry: Map<string, number> = new Map();

  static getInstance(): SEOUtils {
    if (!SEOUtils.instance) {
      SEOUtils.instance = new SEOUtils();
    }
    return SEOUtils.instance;
  }

  /**
   * Load SEO data for a page
   */
  async loadPageSEO(page: string, lang: string = 'en'): Promise<SEOPageData | null> {
    const cacheKey = `${page}:${lang}`;

    // Check cache first
    if (this.cache.has(cacheKey) && this.cacheExpiry.get(cacheKey)! > Date.now()) {
      return this.cache.get(cacheKey)!;
    }

    try {
      const response = await fetch(`/api/v1/content/seo/meta/${page}?lang=${lang}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        const seoData = result.data as SEOPageData;

        // Cache for 1 hour
        this.cache.set(cacheKey, seoData);
        this.cacheExpiry.set(cacheKey, Date.now() + 3600000);

        return seoData;
      }
    } catch (error) {
      console.error('Failed to load SEO data:', error);
    }

    return null;
  }

  /**
   * Update document head with SEO metadata
   */
  updateDocumentHead(seoData: SEOPageData): void {
    // Update title
    document.title = seoData.meta.title;

    // Update or create meta tags
    this.updateMetaTag('description', seoData.meta.description);
    this.updateMetaTag('keywords', seoData.meta.keywords.join(', '));
    this.updateMetaTag('robots', seoData.meta.robots || 'index,follow');
    this.updateMetaTag('viewport', seoData.meta.viewport || 'width=device-width,initial-scale=1');
    this.updateMetaTag('theme-color', seoData.meta.themeColor || '#0f62fe');

    if (seoData.meta.canonical) {
      this.updateLinkTag('canonical', seoData.meta.canonical);
    }

    // Update OpenGraph tags
    Object.entries(seoData.openGraph).forEach(([property, content]) => {
      this.updateMetaProperty(property, content);
    });

    // Update Twitter Card tags
    Object.entries(seoData.twitter).forEach(([name, content]) => {
      this.updateMetaName(name, content);
    });

    // Update structured data
    this.updateStructuredData(seoData.structuredData);
  }

  /**
   * Generate hreflang tags for international SEO
   */
  generateHreflangTags(currentPath: string): void {
    const baseUrl = window.location.origin;
    const hreflangEntries = [
      { hreflang: 'en', href: `${baseUrl}/${currentPath}` },
      { hreflang: 'pt-BR', href: `${baseUrl}/pt/${currentPath}` },
      { hreflang: 'x-default', href: `${baseUrl}/${currentPath}` },
    ];

    // Remove existing hreflang links
    const existingLinks = document.querySelectorAll('link[hreflang]');
    existingLinks.forEach(link => link.remove());

    // Add new hreflang links
    hreflangEntries.forEach(entry => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = entry.hreflang;
      link.href = entry.href;
      document.head.appendChild(link);
    });
  }

  /**
   * Optimize images for SEO
   */
  optimizeImages(): void {
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      // Add loading="lazy" for better performance
      if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }

      // Ensure alt attributes exist
      if (!img.hasAttribute('alt')) {
        console.warn('Image missing alt attribute:', img.src);
        img.setAttribute('alt', 'Educational content image');
      }

      // Add width and height for CLS optimization
      if (!img.hasAttribute('width') || !img.hasAttribute('height')) {
        img.addEventListener('load', () => {
          if (!img.hasAttribute('width')) {
            img.setAttribute('width', img.naturalWidth.toString());
          }
          if (!img.hasAttribute('height')) {
            img.setAttribute('height', img.naturalHeight.toString());
          }
        });
      }
    });
  }

  /**
   * Track Core Web Vitals
   */
  trackCoreWebVitals(): void {
    // Largest Contentful Paint (LCP)
    this.trackLCP();

    // First Input Delay (FID)
    this.trackFID();

    // Cumulative Layout Shift (CLS)
    this.trackCLS();

    // First Contentful Paint (FCP)
    this.trackFCP();
  }

  /**
   * Preload critical resources
   */
  preloadCriticalResources(): void {
    // Preload critical CSS
    const criticalCSS = ['/styles/critical.css', '/styles/carbon.css'];

    criticalCSS.forEach(href => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'style';
      link.href = href;
      link.onload = () => {
        link.onload = null;
        link.rel = 'stylesheet';
      };
      document.head.appendChild(link);
    });

    // IBM Plex fonts are bundled locally via @ibm/plex package in CSS imports
    // No manual font preloading needed - they are loaded through CSS imports
  }

  private updateMetaTag(name: string, content: string): void {
    let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = name;
      document.head.appendChild(meta);
    }
    meta.content = content;
  }

  private updateMetaProperty(property: string, content: string): void {
    let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.content = content;
  }

  private updateMetaName(name: string, content: string): void {
    let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = name;
      document.head.appendChild(meta);
    }
    meta.content = content;
  }

  private updateLinkTag(rel: string, href: string): void {
    let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = href;
  }

  private updateStructuredData(structuredData: StructuredData[]): void {
    // Remove existing structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add new structured data
    structuredData.forEach(data => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    });
  }

  private trackLCP(): void {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver(entryList => {
          const entries = entryList.getEntries();
          const lastEntry = entries[entries.length - 1];

          // Send LCP data to analytics
          this.sendWebVitalMetric('LCP', lastEntry.startTime);
        });

        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (error) {
        console.warn('LCP tracking failed:', error);
      }
    }
  }

  private trackFID(): void {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver(entryList => {
          const entries = entryList.getEntries();
          entries.forEach((entry: any) => {
            // Send FID data to analytics
            this.sendWebVitalMetric('FID', entry.processingStart - entry.startTime);
          });
        });

        observer.observe({ entryTypes: ['first-input'] });
      } catch (error) {
        console.warn('FID tracking failed:', error);
      }
    }
  }

  private trackCLS(): void {
    if ('PerformanceObserver' in window) {
      try {
        let clsValue = 0;
        const observer = new PerformanceObserver(entryList => {
          const entries = entryList.getEntries();
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });

          // Send CLS data to analytics
          this.sendWebVitalMetric('CLS', clsValue);
        });

        observer.observe({ entryTypes: ['layout-shift'] });
      } catch (error) {
        console.warn('CLS tracking failed:', error);
      }
    }
  }

  private trackFCP(): void {
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver(entryList => {
          const entries = entryList.getEntries();
          entries.forEach((entry: any) => {
            // Send FCP data to analytics
            this.sendWebVitalMetric('FCP', entry.startTime);
          });
        });

        observer.observe({ entryTypes: ['paint'] });
      } catch (error) {
        console.warn('FCP tracking failed:', error);
      }
    }
  }

  private sendWebVitalMetric(name: string, value: number): void {
    // Send to analytics service
    fetch('/api/v1/marketing/analytics/web-vitals', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        metric: name,
        value: value,
        timestamp: new Date().toISOString(),
        page: window.location.pathname,
      }),
    }).catch(error => {
      console.warn('Failed to send web vital metric:', error);
    });
  }
}
